"use strict";
/**
 * APITable <https://github.com/apitable/apitable>
 * Copyright (C) 2022 APITable Ltd. <https://apitable.com>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.IsError = exports.FormulaError = exports.Switch = exports.Not = exports.Xor = exports.And = exports.Or = exports.False = exports.True = exports.Blank = exports.If = void 0;
const basic_1 = require("./basic");
const types_1 = require("../../types");
const dayjs_1 = __importDefault(require("dayjs"));
const helper_1 = require("../../formula_parser/helper");
const params_count_error_1 = require("../../formula_parser/errors/params_count.error");
class LogicalFunc extends basic_1.FormulaFunc {
}
LogicalFunc.type = types_1.FormulaFuncType.Logical;
function isArrayParam(params) {
    if (params.length !== 1) {
        return false;
    }
    if (params[0].node.valueType === types_1.BasicValueType.Array) {
        return true;
    }
    return false;
}
class If extends LogicalFunc {
    static validateParams(params) {
        if (params.length < 3) {
            throw new params_count_error_1.ParamsCountError(params_count_error_1.ParamsErrorType.NotEquals, 'IF', 3);
        }
    }
    static getReturnType(params) {
        params && this.validateParams(params);
        if (!params) {
            return types_1.BasicValueType.String;
        }
        const [, value1Node, value2Node] = params;
        if ([value1Node.valueType, value2Node.valueType].some(v => v === types_1.BasicValueType.Array)) {
            return types_1.BasicValueType.String;
        }
        // If one of the two values is 'BLANK', the result type is determined by the type of the other
        if (value1Node.token.value.toUpperCase() === 'BLANK') {
            return value2Node.valueType;
        }
        if (value2Node.token.value.toUpperCase() === 'BLANK') {
            return value1Node.valueType;
        }
        // When both value parameters are Number/Boolean/DateTime/String, the return value of the corresponding type is inferred
        if (value1Node.valueType === value2Node.valueType) {
            return value1Node.valueType;
        }
        return types_1.BasicValueType.String;
    }
    static func(params) {
        const [logical, value1Param, value2Param] = params;
        return logical.value ? value1Param.value : value2Param.value;
    }
}
exports.If = If;
If.acceptValueType = new Set([types_1.BasicValueType.Array, types_1.BasicValueType.DateTime, ...basic_1.FormulaFunc.acceptValueType]);
class Blank extends LogicalFunc {
    static validateParams(_params) {
        //
    }
    static getReturnType(params) {
        params && this.validateParams(params);
        return types_1.BasicValueType.String;
    }
    static func() {
        return null;
    }
}
exports.Blank = Blank;
class True extends LogicalFunc {
    static validateParams(_params) {
        //
    }
    static getReturnType(params) {
        params && this.validateParams(params);
        return types_1.BasicValueType.Boolean;
    }
    static func() {
        return true;
    }
}
exports.True = True;
class False extends LogicalFunc {
    static validateParams(_params) {
        //
    }
    static getReturnType(params) {
        params && this.validateParams(params);
        return types_1.BasicValueType.Boolean;
    }
    static func() {
        return false;
    }
}
exports.False = False;
class Or extends LogicalFunc {
    static validateParams(params) {
        if (params.length < 1) {
            throw new params_count_error_1.ParamsCountError(params_count_error_1.ParamsErrorType.AtLeastCount, 'OR', 1);
        }
    }
    static getReturnType(params) {
        params && this.validateParams(params);
        return types_1.BasicValueType.Boolean;
    }
    static func(params) {
        params = (0, helper_1.handleLookupNullValue)(params);
        if (isArrayParam(params)) {
            if (!params[0].value) {
                return false;
            }
            return params[0].value.some(v => Boolean(v));
        }
        return params.some(v => Boolean(v.value));
    }
}
exports.Or = Or;
Or.acceptValueType = new Set([types_1.BasicValueType.Array, ...basic_1.FormulaFunc.acceptValueType]);
class And extends LogicalFunc {
    static validateParams(params) {
        if (params.length < 1) {
            throw new params_count_error_1.ParamsCountError(params_count_error_1.ParamsErrorType.AtLeastCount, 'AND', 1);
        }
    }
    static getReturnType(params) {
        params && this.validateParams(params);
        return types_1.BasicValueType.Boolean;
    }
    static func(params) {
        params = (0, helper_1.handleLookupNullValue)(params);
        if (isArrayParam(params)) {
            if (!params[0].value) {
                return false;
            }
            return params[0].value.every(v => Boolean(v));
        }
        return params.every(v => Boolean(v.value));
    }
}
exports.And = And;
And.acceptValueType = new Set([types_1.BasicValueType.Array, ...basic_1.FormulaFunc.acceptValueType]);
class Xor extends LogicalFunc {
    static validateParams(params) {
        if (params.length < 1) {
            throw new params_count_error_1.ParamsCountError(params_count_error_1.ParamsErrorType.AtLeastCount, 'XOR', 1);
        }
    }
    static getReturnType(params) {
        params && this.validateParams(params);
        return types_1.BasicValueType.Boolean;
    }
    static func(params) {
        let count;
        params = (0, helper_1.handleLookupNullValue)(params);
        if (isArrayParam(params)) {
            if (!params[0].value) {
                return false;
            }
            count = params[0].value.reduce((prev, cur) => cur ? prev + 1 : prev, 0);
        }
        else {
            count = params.reduce((prev, cur) => cur.value ? prev + 1 : prev, 0);
        }
        return Boolean(count & 1);
    }
}
exports.Xor = Xor;
Xor.acceptValueType = new Set([types_1.BasicValueType.Array, ...basic_1.FormulaFunc.acceptValueType]);
class Not extends LogicalFunc {
    static validateParams(params) {
        if (params.length < 1) {
            throw new params_count_error_1.ParamsCountError(params_count_error_1.ParamsErrorType.NotEquals, 'NOT', 1);
        }
    }
    static getReturnType(params) {
        params && this.validateParams(params);
        return types_1.BasicValueType.Boolean;
    }
    static func(params) {
        params = (0, helper_1.handleLookupNullValue)(params);
        const logical1Param = params[0].value;
        return !logical1Param;
    }
}
exports.Not = Not;
class Switch extends LogicalFunc {
    static validateParams(params) {
        if (params.length < 2) {
            throw new params_count_error_1.ParamsCountError(params_count_error_1.ParamsErrorType.AtLeastCount, 'SWITCH', 2);
        }
    }
    static getReturnType(params) {
        params && this.validateParams(params);
        if (!params) {
            return types_1.BasicValueType.String;
        }
        // Only when the return type of the value of all matched expressions is Number/Boolean/DateTime/String, 
        // it is inferred as the return value of the corresponding type
        const argsLength = params.length - 1;
        let resultType = argsLength & 1 ? params[argsLength].valueType : null;
        for (let i = 2; i <= argsLength; i += 2) {
            const curParams = params[i];
            // If the matched expression contains BLANK, it is determined by other return types
            if (curParams.token.value.toUpperCase() === 'BLANK')
                continue;
            if (resultType != null && resultType !== curParams.valueType) {
                return types_1.BasicValueType.String;
            }
            resultType = curParams.valueType;
        }
        resultType = resultType || types_1.BasicValueType.String;
        return resultType === types_1.BasicValueType.Array ? types_1.BasicValueType.String : resultType;
    }
    static func(params) {
        params = (0, helper_1.handleLookupNullValue)(params);
        let targetValue = params[0].value;
        const argsLength = params.length - 1;
        const switchCount = Math.floor(argsLength / 2);
        const defaultValue = argsLength & 1 ? params[argsLength].value : null;
        const isDateTimeType = params[0].node.innerValueType === types_1.BasicValueType.DateTime; // Do special handling for DateTime types
        // Specially handle the matching of Array type fields to BLANK
        const isEmptyArray = (param) => { var _a; return param.node.valueType === types_1.BasicValueType.Array && !((_a = param.value) === null || _a === void 0 ? void 0 : _a.length); };
        if (isDateTimeType) {
            targetValue = (0, dayjs_1.default)(targetValue).valueOf();
        }
        if (isEmptyArray(params[0])) {
            targetValue = null;
        }
        if (switchCount > 0) {
            for (let i = 0; i < switchCount; i++) {
                const currentParam = params[i * 2 + 1];
                let currentValue = currentParam.value;
                if (isDateTimeType) {
                    currentValue = (0, dayjs_1.default)(currentValue).valueOf();
                }
                if (isEmptyArray(currentParam)) {
                    currentValue = null;
                }
                if (targetValue === currentValue) {
                    return params[i * 2 + 2].value;
                }
            }
        }
        return defaultValue;
    }
}
exports.Switch = Switch;
Switch.acceptValueType = new Set([types_1.BasicValueType.DateTime, ...basic_1.FormulaFunc.acceptValueType]);
// All equations' running errors or manual triggers will inherit this class
class FormulaError extends LogicalFunc {
    static validateParams(_params) {
        //
    }
    static getReturnType(params) {
        params && this.validateParams(params);
        return types_1.BasicValueType.String;
    }
    static func(params) {
        var _a;
        const errText = (_a = params[0]) === null || _a === void 0 ? void 0 : _a.value;
        throw new basic_1.FormulaBaseError(errText || '');
    }
}
exports.FormulaError = FormulaError;
class IsError extends LogicalFunc {
    static validateParams(params) {
        if (params.length < 1) {
            throw new params_count_error_1.ParamsCountError(params_count_error_1.ParamsErrorType.NotEquals, 'IS_ERROR', 1);
        }
    }
    static getReturnType(params) {
        params && this.validateParams(params);
        return types_1.BasicValueType.Boolean;
    }
    static func(params) {
        const value = params[0].value;
        const isError = value instanceof basic_1.FormulaBaseError || isNaN(value) || value === Infinity || value === -Infinity;
        return isError;
    }
}
exports.IsError = IsError;
//# sourceMappingURL=logical.js.map